// components/CitationChip.tsx — ported from docs/superpowers/design-2026-04-21/messages.jsx

import type { JSX } from 'react';
import { useTokens } from '../state/theme';
import type { ProfCitationV1 } from '../types/prof-citation';

export type Citation = ProfCitationV1;

interface CitationChipValue {
  id: string;
  argomentoName: string;
  principleName: string;
  version?: number;
  destination?: ProfCitationV1['destination'];
}

export interface CitationChipProps {
  cite: CitationChipValue;
  onClick?: () => void;
}

export function CitationChip({ cite, onClick }: CitationChipProps): JSX.Element {
  const t = useTokens();
  const opensAppendice = cite.version === 1 && cite.destination?.section === 'appendice';
  // V28 S1 — the prototype `.chip.cite` (kit.css §5 / prof-chat.html): a small
  // lavender pill carrying the brand as a soft gradient tint (one of the "five
  // moments of color"), hairline violet edge, brand-ink text, weight 700, and a
  // bookmark glyph. Mode-aware to keep every token key live under the F1
  // light-only lockdown; resolves to the light `--brand-grad-soft` in practice.
  const tintBg = t.mode === 'dark'
    ? 'linear-gradient(135deg, rgba(124,90,255,0.18), rgba(255,90,216,0.16))'
    : 'linear-gradient(90deg, #f0e9ff, #fbeefa)'; // prototype --brand-grad-soft
  const tintBorder = t.mode === 'dark' ? 'rgba(124,90,255,0.30)' : 'rgba(105, 67, 255, 0.14)';
  return (
    <button
      onClick={onClick}
      aria-label={opensAppendice
        ? `Fonte: ${cite.principleName} — Apri in Appendice`
        : `Principio: ${cite.principleName} — toccare per espandere`}
      style={{
        // prototype `.chip` + `.chip.cite`: gradient-tint pill, brand-ink, 36px
        // min tap height, weight 700 (cite variant), nowrap.
        display: 'inline-flex', alignItems: 'center', gap: 6,
        minHeight: 36, padding: '0 12px',
        background: tintBg, border: `1px solid ${tintBorder}`,
        borderRadius: t.radius.pill, cursor: 'pointer',
        fontFamily: t.fonts.body, fontSize: 13, color: t.accentInk,
        fontWeight: 700, letterSpacing: -0.05, whiteSpace: 'nowrap',
        transition: 'filter .16s',
      }}
      onMouseOver={(e) => { e.currentTarget.style.filter = 'brightness(0.98)'; }}
      onMouseOut={(e) => { e.currentTarget.style.filter = 'none'; }}
    >
      {/* prototype bookmark glyph (prof-chat.html `.chip.cite svg`) */}
      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke={t.accentInk} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M4 19.5A2.5 2.5 0 016.5 17H20"/>
        <path d="M6.5 2H20v20H6.5A2.5 2.5 0 014 19.5v-15A2.5 2.5 0 016.5 2z"/>
      </svg>
      {opensAppendice && <span style={{ color: t.accentInk, opacity: 0.72 }}>Fonte</span>}
      {cite.argomentoName && (
        <span style={{ color: t.accentInk, opacity: 0.72 }}>{cite.argomentoName}</span>
      )}
      <span style={{ color: t.accentInk, fontWeight: 700 }}>{cite.principleName}</span>
      {opensAppendice && <span style={{ color: t.accentInk }}>Apri in Appendice</span>}
    </button>
  );
}
